import { useCallback, useEffect, useState } from 'react';
import type { ElementRect } from './custom-popover-utils.js';

function toNumber(value: string | undefined): number {
  if (!value) return 0;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function extractTranslate(translate: string) {
  if (!translate || translate === 'none') return { translateX: 0, translateY: 0 };
  const [x, y] = translate.split(' ');
  return { translateX: toNumber(x), translateY: toNumber(y) };
}

export type ElementRectContext = 'anchor' | 'popoverPaper';

/**
 * The paper is measured from its computed style rather than its bounding box:
 * the box already includes the entry transform, which would make the arrow
 * jump while the popover animates open.
 */
export function useElementRect(
  element: HTMLElement | null,
  context: ElementRectContext,
  open: boolean,
): ElementRect | null {
  const [rect, setRect] = useState<ElementRect | null>(null);

  const updateRect = useCallback(() => {
    if (!element || !open) return;

    if (context === 'popoverPaper') {
      const { top, left, width, height, marginTop, marginLeft, translate } =
        getComputedStyle(element);
      const { translateX, translateY } = extractTranslate(translate);

      setRect({
        width: toNumber(width),
        height: toNumber(height),
        top: toNumber(top) + toNumber(marginTop) + translateY,
        left: toNumber(left) + toNumber(marginLeft) + translateX,
      });
      return;
    }

    const domRect = element.getBoundingClientRect();
    setRect({
      top: domRect.top,
      left: domRect.left,
      width: domRect.width,
      height: domRect.height,
    });
  }, [context, element, open]);

  useEffect(() => {
    if (!element || !open) return;

    updateRect();

    const resizeObserver = new ResizeObserver(updateRect);
    resizeObserver.observe(element);
    window.addEventListener('resize', updateRect, { passive: true });
    window.addEventListener('scroll', updateRect, { capture: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, { capture: true });
    };
  }, [element, open, updateRect]);

  return rect;
}
